import { useState, useCallback, useMemo } from 'react';
import {
  AdminHome,
  AdminHomeDetail,
  AdminChatConversation,
  AdminChatMessage,
  AdminBugReport,
  AdminReferral,
  ContentFilters,
} from '../../types/admin';

// ============================================================================
// MOCK DATA GENERATION
// ============================================================================

const FIRST_NAMES = [
  'James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda',
  'William', 'Barbara', 'David', 'Elizabeth', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Christopher', 'Karen', 'Daniel', 'Nancy', 'Matthew', 'Lisa',
];

const LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Wilson', 'Anderson', 'Thomas', 'Taylor',
];

const PROPERTY_TYPES = [
  'Modern Downtown Condo',
  'Suburban Family Home',
  'Luxury Waterfront Estate',
  'Historic Victorian Home',
  'Contemporary Townhouse',
  'Rustic Farmhouse',
  'Beachfront Villa',
  'Mountain Cabin Retreat',
  'Urban Loft Apartment',
  'Colonial Style Home',
  'Mediterranean Villa',
  'Craftsman Bungalow',
  'Ranch Style Home',
  'Tudor Revival Home',
  'Mid-Century Modern',
];

const CITIES = [
  { city: 'New York', state: 'NY', zip: '10001' },
  { city: 'Los Angeles', state: 'CA', zip: '90001' },
  { city: 'Chicago', state: 'IL', zip: '60601' },
  { city: 'Houston', state: 'TX', zip: '77001' },
  { city: 'Phoenix', state: 'AZ', zip: '85001' },
  { city: 'Philadelphia', state: 'PA', zip: '19101' },
  { city: 'San Antonio', state: 'TX', zip: '78201' },
  { city: 'San Diego', state: 'CA', zip: '92101' },
  { city: 'Dallas', state: 'TX', zip: '75201' },
  { city: 'Austin', state: 'TX', zip: '78701' },
  { city: 'San Jose', state: 'CA', zip: '95101' },
  { city: 'Jacksonville', state: 'FL', zip: '32099' },
  { city: 'Fort Worth', state: 'TX', zip: '76101' },
  { city: 'Columbus', state: 'OH', zip: '43004' },
  { city: 'Charlotte', state: 'NC', zip: '28201' },
  { city: 'Indianapolis', state: 'IN', zip: '46201' },
  { city: 'Seattle', state: 'WA', zip: '98101' },
  { city: 'Denver', state: 'CO', zip: '80201' },
  { city: 'Boston', state: 'MA', zip: '02101' },
  { city: 'Portland', state: 'OR', zip: '97201' },
];

const STREET_NAMES = [
  'Main St', 'Oak Ave', 'Maple Dr', 'Cedar Ln', 'Pine St', 'Elm Ave', 'Washington Blvd',
  'Park Place', 'Lake Shore Dr', 'Sunset Blvd', 'Highland Ave', 'Valley View Rd',
  'Mountain Vista Way', 'Ocean Breeze Ln', 'Forest Hill Dr', 'River Rd', 'Garden St',
  'Meadow Ln', 'Ridge Rd', 'Summit Ave',
];

const FLAG_REASONS = [
  'Inappropriate images',
  'Spam listing',
  'Misleading information',
  'Duplicate property',
  'Privacy violation',
  'Offensive content',
  'Suspected fraud',
  'Copyright violation',
];

const BUG_CATEGORIES = [
  'UI/UX Issue',
  'Performance',
  'Feature Request',
  'Data Error',
  'Security Concern',
  'Mobile App',
  'Integration Issue',
  'Billing',
  'Authentication',
  'Export/Import',
];

const BUG_SUBJECTS = [
  'Image upload fails on mobile',
  'Home list not loading',
  'Search functionality broken',
  'Profile picture not updating',
  'Email notifications not received',
  'Payment processing error',
  'Dashboard showing incorrect data',
  'Export feature missing properties',
  'Chat messages not syncing',
  'App crashes when uploading large files',
  'Filter options not working',
  'Unable to delete archived homes',
  'Task completion not saving',
  'Login timeout too short',
  'Map markers not displaying',
];

const CHAT_MESSAGES_SAMPLES = [
  'Hi, I have a question about the property',
  'When is the best time to schedule a viewing?',
  'What are the HOA fees for this property?',
  'Can you send me more details about the neighborhood?',
  'I am interested in making an offer',
  'Are pets allowed in this property?',
  'What is included in the sale?',
  'How long has this been on the market?',
  'Is there any room for negotiation on the price?',
  'What are the schools like in this area?',
];

function randomDate(start: Date, end: Date): string {
  const date = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
  return date.toISOString();
}

function randomName(): { firstName: string; lastName: string; fullName: string; email: string } {
  const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
  const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
  return {
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
  };
}

function randomAddress(): string {
  const num = Math.floor(Math.random() * 9999) + 100;
  const street = STREET_NAMES[Math.floor(Math.random() * STREET_NAMES.length)];
  const location = CITIES[Math.floor(Math.random() * CITIES.length)];
  return `${num} ${street}, ${location.city}, ${location.state} ${location.zip}`;
}

function generateMockHomes(count: number): AdminHome[] {
  const homes: AdminHome[] = [];
  const now = new Date();
  const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());

  for (let i = 0; i < count; i++) {
    const owner = randomName();
    const propertyType = PROPERTY_TYPES[Math.floor(Math.random() * PROPERTY_TYPES.length)];
    const createdAt = randomDate(oneYearAgo, now);
    const status: 'active' | 'archived' = Math.random() > 0.3 ? 'active' : 'archived';

    // 8-10% flagged
    const isFlagged = Math.random() < 0.09;
    const imagesCount = Math.floor(Math.random() * 25) + 5;

    const home: AdminHome = {
      id: `home_${i + 1}`,
      name: propertyType,
      address: randomAddress(),
      owner_email: owner.email,
      owner_name: owner.fullName,
      owner_picture: `https://i.pravatar.cc/150?img=${(i % 70) + 1}`,
      created_at: createdAt,
      status,
      flagged: isFlagged,
      images_count: imagesCount,
      preview_image: `https://picsum.photos/seed/${i}/400/300`,
    };

    if (isFlagged) {
      home.flag_reason = FLAG_REASONS[Math.floor(Math.random() * FLAG_REASONS.length)];
      home.flagged_at = randomDate(new Date(createdAt), now);
    }

    homes.push(home);
  }

  return homes;
}

function generateMockConversations(count: number): AdminChatConversation[] {
  const conversations: AdminChatConversation[] = [];
  const now = new Date();
  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());

  for (let i = 0; i < count; i++) {
    const agent = randomName();
    const client = randomName();
    const type: 'agent_client' | 'group' = Math.random() > 0.2 ? 'agent_client' : 'group';
    const messageCount = Math.floor(Math.random() * 100) + 10;
    const lastActivity = randomDate(sixMonthsAgo, now);
    const isFlagged = Math.random() < 0.08;

    const participants = type === 'agent_client'
      ? [
          { name: agent.fullName, email: agent.email, picture: `https://i.pravatar.cc/150?img=${(i % 70) + 1}`, role: 'Agent' },
          { name: client.fullName, email: client.email, picture: `https://i.pravatar.cc/150?img=${((i + 1) % 70) + 1}` },
        ]
      : [
          { name: agent.fullName, email: agent.email, picture: `https://i.pravatar.cc/150?img=${(i % 70) + 1}`, role: 'Agent' },
          { name: client.fullName, email: client.email, picture: `https://i.pravatar.cc/150?img=${((i + 1) % 70) + 1}` },
          { name: randomName().fullName, email: randomName().email, picture: `https://i.pravatar.cc/150?img=${((i + 2) % 70) + 1}` },
        ];

    const conversation: AdminChatConversation = {
      id: `conv_${i + 1}`,
      participants,
      type,
      message_count: messageCount,
      last_message: CHAT_MESSAGES_SAMPLES[Math.floor(Math.random() * CHAT_MESSAGES_SAMPLES.length)],
      last_activity: lastActivity,
      flagged: isFlagged,
    };

    if (isFlagged) {
      conversation.flag_reason = 'Inappropriate language reported by user';
    }

    conversations.push(conversation);
  }

  return conversations;
}

function generateMockBugReports(count: number): AdminBugReport[] {
  const reports: AdminBugReport[] = [];
  const now = new Date();
  const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());


  // Distribution: 40% new, 30% in_progress, 20% resolved, 10% closed
  const statusWeights = [0.40, 0.30, 0.20, 0.10];

  for (let i = 0; i < count; i++) {
    const reporter = randomName();
    const createdAt = randomDate(threeMonthsAgo, now);

    // Weighted status selection
    const statusRand = Math.random();
    let status: 'new' | 'in_progress' | 'resolved' | 'closed';
    if (statusRand < statusWeights[0]) status = 'new';
    else if (statusRand < statusWeights[0] + statusWeights[1]) status = 'in_progress';
    else if (statusRand < statusWeights[0] + statusWeights[1] + statusWeights[2]) status = 'resolved';
    else status = 'closed';

    // Severity distribution: 50% low, 30% medium, 15% high, 5% critical
    const severityRand = Math.random();
    let severity: 'low' | 'medium' | 'high' | 'critical';
    if (severityRand < 0.50) severity = 'low';
    else if (severityRand < 0.80) severity = 'medium';
    else if (severityRand < 0.95) severity = 'high';
    else severity = 'critical';

    const category = BUG_CATEGORIES[Math.floor(Math.random() * BUG_CATEGORIES.length)];
    const subject = BUG_SUBJECTS[Math.floor(Math.random() * BUG_SUBJECTS.length)];

    const browsers = ['Chrome 120', 'Safari 17', 'Firefox 121', 'Edge 120'];
    const oses = ['Windows 11', 'macOS 14', 'iOS 17', 'Android 14', 'Ubuntu 22.04'];

    const report: AdminBugReport = {
      id: `bug_${i + 1}`,
      user_email: reporter.email,
      user_name: reporter.fullName,
      category,
      subject,
      description: `User reported: ${subject}. This appears to be affecting ${category.toLowerCase()}. Additional context: The issue occurs consistently when performing the described action.`,
      severity,
      status,
      created_at: createdAt,
      updated_at: status !== 'new' ? randomDate(new Date(createdAt), now) : undefined,
      assigned_to: status === 'in_progress' || status === 'resolved' ? 'engineering@cribcritic.com' : undefined,
      reporter_context: {
        browser: browsers[Math.floor(Math.random() * browsers.length)],
        os: oses[Math.floor(Math.random() * oses.length)],
        url: `/app/${['dashboard', 'homes', 'tasks', 'settings'][Math.floor(Math.random() * 4)]}`,
      },
    };

    reports.push(report);
  }

  // Sort by created_at descending (newest first)
  reports.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return reports;
}

function generateMockReferrals(count: number): AdminReferral[] {
  const referrals: AdminReferral[] = [];
  const now = new Date();
  const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());

  // Distribution: 50% pending, 35% approved, 15% rejected
  const statusWeights = [0.50, 0.35, 0.15];

  for (let i = 0; i < count; i++) {
    const referrer = randomName();
    const referred = randomName();
    const submissionDate = randomDate(twoMonthsAgo, now);

    const statusRand = Math.random();
    let status: 'pending' | 'approved' | 'rejected';
    if (statusRand < statusWeights[0]) status = 'pending';
    else if (statusRand < statusWeights[0] + statusWeights[1]) status = 'approved';
    else status = 'rejected';

    const referral: AdminReferral = {
      id: `ref_${i + 1}`,
      referrer_email: referrer.email,
      referrer_name: referrer.fullName,
      referred_email: referred.email,
      referred_name: referred.fullName,
      status,
      submission_date: submissionDate,
    };

    if (status === 'approved') {
      referral.approval_date = randomDate(new Date(submissionDate), now);
      referral.notes = 'Referral verified and approved';
    } else if (status === 'rejected') {
      referral.rejection_date = randomDate(new Date(submissionDate), now);
      referral.rejection_reason = 'Email already registered in system';
    }

    referrals.push(referral);
  }

  return referrals;
}

function generateConversationMessages(conversationId: string, participants: AdminChatConversation['participants']): AdminChatMessage[] {
  const messages: AdminChatMessage[] = [];
  const messageCount = Math.floor(Math.random() * 20) + 10;
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());

  for (let i = 0; i < messageCount; i++) {
    const participant = participants[Math.floor(Math.random() * participants.length)];
    const timestamp = randomDate(startDate, now);

    messages.push({
      id: `msg_${conversationId}_${i + 1}`,
      sender: participant.name,
      sender_email: participant.email,
      sender_picture: participant.picture,
      content: CHAT_MESSAGES_SAMPLES[Math.floor(Math.random() * CHAT_MESSAGES_SAMPLES.length)],
      timestamp,
      flagged: false,
    });
  }

  // Sort chronologically
  messages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  return messages;
}

function generateHomeDetail(home: AdminHome): AdminHomeDetail {
  const imageCount = home.images_count || 8;
  const images = Array.from({ length: imageCount }, (_, i) => ({
    url: `https://picsum.photos/seed/${home.id}_${i}/800/600`,
    caption: i === 0 ? 'Front exterior view' : i === 1 ? 'Living room' : i === 2 ? 'Kitchen' : undefined,
    uploaded_at: randomDate(new Date(home.created_at), new Date()),
  }));

  return {
    ...home,
    images,
    description: `Beautiful ${home.name.toLowerCase()} located in a prime area. This property features modern amenities and stunning architecture.`,
    owner_id: `user_${home.id.split('_')[1]}`,
  };
}

// Generate mock data once
const MOCK_HOMES = generateMockHomes(90);
const MOCK_CONVERSATIONS = generateMockConversations(45);
const MOCK_BUG_REPORTS = generateMockBugReports(35);
const MOCK_REFERRALS = generateMockReferrals(25);

/**
 * useAdminContent - Hook for content moderation operations
 *
 * Provides:
 * - Homes moderation with flag/unflag/delete
 * - Chat conversation moderation
 * - Bug reports queue management
 * - Referral approval/rejection
 * - Filtering and search capabilities
 */
export function useAdminContent() {
  const [homes, setHomes] = useState<AdminHome[]>(MOCK_HOMES);
  const [conversations] = useState<AdminChatConversation[]>(MOCK_CONVERSATIONS);
  const [bugReports, setBugReports] = useState<AdminBugReport[]>(MOCK_BUG_REPORTS);
  const [referrals, setReferrals] = useState<AdminReferral[]>(MOCK_REFERRALS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [homesFilters, setHomesFilters] = useState<ContentFilters>({});
  const [conversationsFilters, setConversationsFilters] = useState<ContentFilters>({});
  const [bugReportsFilters, setBugReportsFilters] = useState<ContentFilters>({});
  const [referralsFilters, setReferralsFilters] = useState<ContentFilters>({});

  // ============================================================================
  // FILTERED DATA
  // ============================================================================

  const filteredHomes = useMemo(() => {
    let result = [...homes];

    if (homesFilters.search) {
      const searchLower = homesFilters.search.toLowerCase();
      result = result.filter(
        (h) =>
          h.name.toLowerCase().includes(searchLower) ||
          h.address?.toLowerCase().includes(searchLower) ||
          h.owner_name.toLowerCase().includes(searchLower) ||
          h.owner_email.toLowerCase().includes(searchLower)
      );
    }

    if (homesFilters.status) {
      result = result.filter((h) => h.status === homesFilters.status);
    }

    if (homesFilters.flagged !== undefined) {
      result = result.filter((h) => h.flagged === homesFilters.flagged);
    }

    if (homesFilters.date_from) {
      result = result.filter((h) => new Date(h.created_at) >= new Date(homesFilters.date_from!));
    }

    if (homesFilters.date_to) {
      result = result.filter((h) => new Date(h.created_at) <= new Date(homesFilters.date_to!));
    }

    return result;
  }, [homes, homesFilters]);

  const filteredConversations = useMemo(() => {
    let result = [...conversations];

    if (conversationsFilters.search) {
      const searchLower = conversationsFilters.search.toLowerCase();
      result = result.filter((c) =>
        c.participants.some((p) =>
          p.name.toLowerCase().includes(searchLower) ||
          p.email.toLowerCase().includes(searchLower)
        )
      );
    }

    if (conversationsFilters.flagged !== undefined) {
      result = result.filter((c) => c.flagged === conversationsFilters.flagged);
    }

    return result;
  }, [conversations, conversationsFilters]);

  const filteredBugReports = useMemo(() => {
    let result = [...bugReports];

    if (bugReportsFilters.search) {
      const searchLower = bugReportsFilters.search.toLowerCase();
      result = result.filter(
        (r) =>
          r.subject.toLowerCase().includes(searchLower) ||
          r.description.toLowerCase().includes(searchLower) ||
          r.user_email.toLowerCase().includes(searchLower)
      );
    }

    if (bugReportsFilters.status) {
      result = result.filter((r) => r.status === bugReportsFilters.status);
    }

    if (bugReportsFilters.severity) {
      result = result.filter((r) => r.severity === bugReportsFilters.severity);
    }

    if (bugReportsFilters.category) {
      result = result.filter((r) => r.category === bugReportsFilters.category);
    }

    return result;
  }, [bugReports, bugReportsFilters]);

  const filteredReferrals = useMemo(() => {
    let result = [...referrals];

    if (referralsFilters.search) {
      const searchLower = referralsFilters.search.toLowerCase();
      result = result.filter(
        (r) =>
          r.referrer_email.toLowerCase().includes(searchLower) ||
          r.referred_email.toLowerCase().includes(searchLower) ||
          r.referrer_name?.toLowerCase().includes(searchLower) ||
          r.referred_name?.toLowerCase().includes(searchLower)
      );
    }

    if (referralsFilters.status) {
      result = result.filter((r) => r.status === referralsFilters.status);
    }

    return result;
  }, [referrals, referralsFilters]);

  // ============================================================================
  // HOMES ACTIONS
  // ============================================================================

  const flagHome = useCallback(async (homeId: string, reason: string) => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));

      setHomes((prev) =>
        prev.map((h) =>
          h.id === homeId
            ? { ...h, flagged: true, flag_reason: reason, flagged_at: new Date().toISOString() }
            : h
        )
      );

      setIsLoading(false);
      return { success: true };
    } catch (err) {
      setIsLoading(false);
      setError('Failed to flag home');
      return { success: false, error: 'Failed to flag home' };
    }
  }, []);

  const unflagHome = useCallback(async (homeId: string) => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));

      setHomes((prev) =>
        prev.map((h) => {
          if (h.id === homeId) {
            const updated = { ...h, flagged: false };
            delete updated.flag_reason;
            delete updated.flagged_at;
            return updated;
          }
          return h;
        })
      );

      setIsLoading(false);
      return { success: true };
    } catch (err) {
      setIsLoading(false);
      setError('Failed to unflag home');
      return { success: false, error: 'Failed to unflag home' };
    }
  }, []);

  const deleteHome = useCallback(async (homeId: string) => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 800));

      setHomes((prev) => prev.filter((h) => h.id !== homeId));

      setIsLoading(false);
      return { success: true };
    } catch (err) {
      setIsLoading(false);
      setError('Failed to delete home');
      return { success: false, error: 'Failed to delete home' };
    }
  }, []);

  const getHomeDetail = useCallback(
    (homeId: string): AdminHomeDetail | null => {
      const home = homes.find((h) => h.id === homeId);
      if (!home) return null;
      return generateHomeDetail(home);
    },
    [homes]
  );

  // ============================================================================
  // CHAT ACTIONS
  // ============================================================================

  const getConversationMessages = useCallback(
    (conversationId: string): AdminChatMessage[] => {
      const conversation = conversations.find((c) => c.id === conversationId);
      if (!conversation) return [];
      return generateConversationMessages(conversationId, conversation.participants);
    },
    [conversations]
  );

  const flagConversation = useCallback(async (_conversationId: string, _reason: string) => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      // In real implementation, update conversation flag status
      setIsLoading(false);
      return { success: true };
    } catch (err) {
      setIsLoading(false);
      return { success: false, error: 'Failed to flag conversation' };
    }
  }, []);

  // ============================================================================
  // BUG REPORT ACTIONS
  // ============================================================================

  const updateBugReportStatus = useCallback(
    async (reportId: string, status: AdminBugReport['status']) => {
      setIsLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));

        setBugReports((prev) =>
          prev.map((r) =>
            r.id === reportId
              ? { ...r, status, updated_at: new Date().toISOString() }
              : r
          )
        );

        setIsLoading(false);
        return { success: true };
      } catch (err) {
        setIsLoading(false);
        return { success: false, error: 'Failed to update bug report status' };
      }
    },
    []
  );

  const assignBugReport = useCallback(async (reportId: string, assignee: string) => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));

      setBugReports((prev) =>
        prev.map((r) =>
          r.id === reportId
            ? { ...r, assigned_to: assignee, status: 'in_progress', updated_at: new Date().toISOString() }
            : r
        )
      );

      setIsLoading(false);
      return { success: true };
    } catch (err) {
      setIsLoading(false);
      return { success: false, error: 'Failed to assign bug report' };
    }
  }, []);

  // ============================================================================
  // REFERRAL ACTIONS
  // ============================================================================

  const approveReferral = useCallback(async (referralId: string) => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));

      setReferrals((prev) =>
        prev.map((r) =>
          r.id === referralId
            ? { ...r, status: 'approved', approval_date: new Date().toISOString() }
            : r
        )
      );

      setIsLoading(false);
      return { success: true };
    } catch (err) {
      setIsLoading(false);
      return { success: false, error: 'Failed to approve referral' };
    }
  }, []);

  const rejectReferral = useCallback(async (referralId: string, reason: string) => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));

      setReferrals((prev) =>
        prev.map((r) =>
          r.id === referralId
            ? {
                ...r,
                status: 'rejected',
                rejection_date: new Date().toISOString(),
                rejection_reason: reason,
              }
            : r
        )
      );

      setIsLoading(false);
      return { success: true };
    } catch (err) {
      setIsLoading(false);
      return { success: false, error: 'Failed to reject referral' };
    }
  }, []);

  return {
    // Data
    homes: filteredHomes,
    conversations: filteredConversations,
    bugReports: filteredBugReports,
    referrals: filteredReferrals,

    // State
    isLoading,
    error,

    // Filters
    homesFilters,
    setHomesFilters,
    conversationsFilters,
    setConversationsFilters,
    bugReportsFilters,
    setBugReportsFilters,
    referralsFilters,
    setReferralsFilters,

    // Home actions
    flagHome,
    unflagHome,
    deleteHome,
    getHomeDetail,

    // Chat actions
    getConversationMessages,
    flagConversation,

    // Bug report actions
    updateBugReportStatus,
    assignBugReport,

    // Referral actions
    approveReferral,
    rejectReferral,
  };
}
